import { useState, useEffect, useRef } from "react";
import {
  FiDollarSign,
  FiFileText,
  FiTag,
  FiTrash,
  FiImage,
  FiDownload,
  FiUploadCloud,
} from "react-icons/fi";

import {
  getUsers,
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from "../api/api";

import toast from "react-hot-toast";
import "./Expense.css";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const CATEGORIES = [
  { value: "Salaries", label: "Salaries" },
  { value: "Office Rent", label: "Office Rent" },
  { value: "Internet", label: "Internet" },
  { value: "Software", label: "Software" },
  { value: "Advertising", label: "Advertising" },
  { value: "Marketing", label: "Marketing" },
  { value: "Freelancer", label: "Freelancer" },
  { value: "Equipment", label: "Equipment" },
  { value: "Miscellaneous", label: "Miscellaneous" },
];

const PAYMENT_METHODS = [
  { value: "Cash", label: "Cash" },
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Vodafone Cash", label: "Vodafone Cash" },
  { value: "Credit Card", label: "Credit Card" },
];

const categoryLabel = (value) =>
  CATEGORIES.find((c) => c.value === value)?.label || value || "—";

const paymentLabel = (value) =>
  PAYMENT_METHODS.find((p) => p.value === value)?.label || value || "—";

const todayStr = () => new Date().toISOString().split("T")[0];

const emptyForm = () => ({
  date: todayStr(),
  category: "",
  description: "",
  amount: "",
  paidBy: "",
  paymentMethod: "",
  notes: "",
});

const formatEGP = (n) =>
  (Number(n) || 0).toLocaleString("en-US") + " EGP";

// ─────────────────────────────────────────────
// INVOICE URL HELPER
// ─────────────────────────────────────────────

const getInvoiceUrl = (invoicePhotos) => {
  if (!invoicePhotos) return null;

  // String
  if (typeof invoicePhotos === "string") {
    const value = invoicePhotos.trim();

    if (!value) return null;

    try {
      const parsed = JSON.parse(value);

      if (parsed !== value) {
        return getInvoiceUrl(parsed);
      }
    } catch {
      // Normal URL/string
    }

    return value;
  }

  // Array
  if (Array.isArray(invoicePhotos)) {
    if (invoicePhotos.length === 0) return null;

    for (const item of invoicePhotos) {
      const url = getInvoiceUrl(item);

      if (url) {
        return url;
      }
    }

    return null;
  }

  // Object
  if (typeof invoicePhotos === "object") {
    if (typeof invoicePhotos.url === "string") {
      return invoicePhotos.url;
    }

    if (typeof invoicePhotos.secure_url === "string") {
      return invoicePhotos.secure_url;
    }

    if (typeof invoicePhotos.imageUrl === "string") {
      return invoicePhotos.imageUrl;
    }

    if (typeof invoicePhotos.imageURL === "string") {
      return invoicePhotos.imageURL;
    }

    if (typeof invoicePhotos.path === "string") {
      return invoicePhotos.path;
    }

    if (typeof invoicePhotos.location === "string") {
      return invoicePhotos.location;
    }

    if (invoicePhotos.data) {
      const url = getInvoiceUrl(invoicePhotos.data);

      if (url) {
        return url;
      }
    }

    if (invoicePhotos.file) {
      const url = getInvoiceUrl(invoicePhotos.file);

      if (url) {
        return url;
      }
    }

    if (invoicePhotos.image) {
      const url = getInvoiceUrl(invoicePhotos.image);

      if (url) {
        return url;
      }
    }
  }

  return null;
};

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

export default function Expense() {
  const [allExpenses, setAllExpenses] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});

  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);

  const [existingInvoiceUrl, setExistingInvoiceUrl] = useState(null);

  // ✅ جديد: علشان نعرف إن المستخدم مسح الصورة الحالية قصداً
  const [removeInvoice, setRemoveInvoice] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);

  const fileRef = useRef(null);

  // ─────────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────────

  const fetchAll = async () => {
    try {
      setLoading(true);

      const [usersData, expData] = await Promise.all([
        getUsers(),
        getExpenses(1, 200),
      ]);

      const usersList = usersData?.data ?? usersData ?? [];

      const expList =
        expData?.data?.data ??
        expData?.data ??
        expData ??
        [];

      setUsers(Array.isArray(usersList) ? usersList : []);

      setAllExpenses(Array.isArray(expList) ? expList : []);

      setExpenses(Array.isArray(expList) ? expList : []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load expenses"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ─────────────────────────────────────────────
  // FILTERING
  // ─────────────────────────────────────────────

  useEffect(() => {
    let filtered = [...allExpenses];

    if (categoryFilter !== "All") {
      filtered = filtered.filter(
        (expense) => expense.category === categoryFilter
      );
    }

    if (dateFrom || dateTo) {
      filtered = filtered.filter((expense) => {
        if (!expense.date) return false;

        const expenseDate = new Date(expense.date);

        if (dateFrom) {
          const fromDate = new Date(`${dateFrom}T00:00:00`);

          if (expenseDate < fromDate) {
            return false;
          }
        }

        if (dateTo) {
          const toDate = new Date(`${dateTo}T23:59:59`);

          if (expenseDate > toDate) {
            return false;
          }
        }

        return true;
      });
    }

    setExpenses(filtered);
  }, [categoryFilter, dateFrom, dateTo, allExpenses]);

  // ─────────────────────────────────────────────
  // USERS
  // ─────────────────────────────────────────────

  const getUserName = (paidBy) => {
    const id = paidBy?._id || paidBy;

    const user = users.find((item) => item._id === id);

    return user?.userName || user?.name || "—";
  };

  // ─────────────────────────────────────────────
  // SEARCH
  // ─────────────────────────────────────────────

  const filtered = expenses.filter((expense) => {
    const searchValue = search.toLowerCase();

    return (
      (expense.description || "")
        .toLowerCase()
        .includes(searchValue) ||
      getUserName(expense.paidBy)
        .toLowerCase()
        .includes(searchValue)
    );
  });

  // ─────────────────────────────────────────────
  // STATISTICS
  // ─────────────────────────────────────────────

  const totalAmount = filtered.reduce(
    (sum, expense) => sum + (Number(expense.amount) || 0),
    0
  );

  const byCategory = filtered.reduce((acc, expense) => {
    const category = expense.category || "Miscellaneous";

    acc[category] =
      (acc[category] || 0) +
      (Number(expense.amount) || 0);

    return acc;
  }, {});

  const topCategory = Object.entries(byCategory).sort(
    (a, b) => b[1] - a[1]
  )[0];

  // ─────────────────────────────────────────────
  // OPEN FILE PICKER
  // ─────────────────────────────────────────────

  const openFilePicker = () => {
    if (submitting) return;

    fileRef.current?.click();
  };

  // ─────────────────────────────────────────────
  // FILE PICK
  // ─────────────────────────────────────────────

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validate image
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");

      event.target.value = "";
      return;
    }

    // Optional size validation: 10 MB
    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error("Image size must be less than 10 MB");

      event.target.value = "";
      return;
    }

    // Revoke old preview if exists
    if (invoicePreview) {
      URL.revokeObjectURL(invoicePreview);
    }

    const previewUrl = URL.createObjectURL(file);

    setInvoiceFile(file);
    setInvoicePreview(previewUrl);

    // New image means we no longer need to show old invoice
    setExistingInvoiceUrl(null);

    // Picking a new file overrides any pending "remove" intent
    setRemoveInvoice(false);

    console.log("SELECTED INVOICE FILE:", file);
  };

  // ─────────────────────────────────────────────
  // REMOVE SELECTED (NEW, NOT-YET-SAVED) IMAGE
  // ─────────────────────────────────────────────

  const removeInvoiceFile = () => {
    if (invoicePreview) {
      URL.revokeObjectURL(invoicePreview);
    }

    setInvoiceFile(null);
    setInvoicePreview(null);

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  // ─────────────────────────────────────────────
  // REMOVE EXISTING (ALREADY SAVED) INVOICE IMAGE
  // ─────────────────────────────────────────────

  const removeExistingInvoice = () => {
    const confirmed = window.confirm(
      "Delete the current invoice photo? This will remove it when you save."
    );

    if (!confirmed) return;

    setExistingInvoiceUrl(null);
    setRemoveInvoice(true);

    // Make sure no stray new file is attached
    if (invoicePreview) {
      URL.revokeObjectURL(invoicePreview);
    }

    setInvoiceFile(null);
    setInvoicePreview(null);

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  // ─────────────────────────────────────────────
  // OPEN ADD
  // ─────────────────────────────────────────────

  const openAdd = () => {
    setEditingId(null);

    setForm(emptyForm());

    setErrors({});

    if (invoicePreview) {
      URL.revokeObjectURL(invoicePreview);
    }

    setInvoiceFile(null);
    setInvoicePreview(null);

    setExistingInvoiceUrl(null);
    setRemoveInvoice(false);

    if (fileRef.current) {
      fileRef.current.value = "";
    }

    setShowModal(true);
  };

  // ─────────────────────────────────────────────
  // OPEN EDIT
  // ─────────────────────────────────────────────

  const openEdit = (expense) => {
    setEditingId(expense._id);

    setErrors({});

    setForm({
      date: expense.date
        ? expense.date.split("T")[0]
        : todayStr(),

      category: expense.category || "",

      description: expense.description || "",

      amount: expense.amount || "",

      paidBy:
        expense.paidBy?._id ||
        expense.paidBy ||
        "",

      paymentMethod:
        expense.paymentMethod || "",

      notes: expense.notes || "",
    });

    if (invoicePreview) {
      URL.revokeObjectURL(invoicePreview);
    }

    setInvoiceFile(null);

    setInvoicePreview(null);

    setRemoveInvoice(false);

    const invoiceUrl = getInvoiceUrl(
      expense.invoicePhotos
    );

    console.log(
      "EDIT - invoicePhotos:",
      expense.invoicePhotos
    );

    console.log(
      "EDIT - extracted invoice URL:",
      invoiceUrl
    );

    setExistingInvoiceUrl(invoiceUrl);

    if (fileRef.current) {
      fileRef.current.value = "";
    }

    setShowModal(true);
  };

  // ─────────────────────────────────────────────
  // SAVE
  // ─────────────────────────────────────────────

  const handleSave = async () => {
    const newErrors = {};

    if (!form.date) {
      newErrors.date = "Please select a date";
    }

    if (!form.category) {
      newErrors.category = "Please select a category";
    }

    if (!form.amount) {
      newErrors.amount = "Please enter an amount";
    }

    if (
      form.amount &&
      Number(form.amount) <= 0
    ) {
      newErrors.amount =
        "Amount must be a positive number";
    }

    if (!form.paidBy) {
      newErrors.paidBy =
        "Please select an employee";
    }

    if (!form.paymentMethod) {
      newErrors.paymentMethod =
        "Please select a payment method";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setSubmitting(true);

      if (editingId) {
        const res = await updateExpense(
          editingId,
          { ...form, removeInvoice }, // ✅ بعت الفلاج للباك اند
          invoiceFile
        );

        const updated = res?.data ?? res;

        console.log(
          "UPDATED EXPENSE:",
          updated
        );

        setAllExpenses((prev) =>
          prev.map((expense) =>
            expense._id === editingId
              ? {
                  ...expense,
                  ...updated,
                  // ✅ لو المستخدم مسح الصورة (Delete) ومفيش صورة جديدة
                  // اتحطت مكانها، نفضي invoicePhotos محليًا بالقوة —
                  // حتى لو السيرفر رجّع الحقل القديم في الـ response،
                  // عشان عمود Invoice في الجدول يتحدث فورًا وميفضلش
                  // شايل صورة اتمسحت من جوا الـ modal.
                  invoicePhotos:
                    removeInvoice && !invoiceFile
                      ? null
                      : updated?.invoicePhotos ??
                        expense.invoicePhotos,
                }
              : expense
          )
        );

        toast.success("Expense updated");
      } else {
        const res = await createExpense(
          form,
          invoiceFile
        );

        const created = res?.data ?? res;

        console.log(
          "CREATED EXPENSE:",
          created
        );

        setAllExpenses((prev) => [
          created,
          ...prev,
        ]);

        toast.success("Expense created");
      }

      setShowModal(false);

      if (invoicePreview) {
        URL.revokeObjectURL(invoicePreview);
      }

      setInvoiceFile(null);
      setInvoicePreview(null);
      setExistingInvoiceUrl(null);
      setRemoveInvoice(false);

      if (fileRef.current) {
        fileRef.current.value = "";
      }
    } catch (err) {
      console.error(
        "SAVE EXPENSE ERROR:",
        err
      );

      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save expense"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────
  // DELETE (WHOLE EXPENSE)
  // ─────────────────────────────────────────────

  const handleDelete = async (id, event) => {
    event.stopPropagation();

    const confirmed = window.confirm(
      "Delete this expense?"
    );

    if (!confirmed) return;

    try {
      await deleteExpense(id);

      setAllExpenses((prev) =>
        prev.filter(
          (expense) => expense._id !== id
        )
      );

      toast.success("Deleted");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete"
      );
    }
  };

  // ─────────────────────────────────────────────
  // OPEN IMAGE MODAL
  // ─────────────────────────────────────────────

  const openImageModal = (
    imageUrl,
    event = null
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!imageUrl) {
      toast.error(
        "Invoice image is not available"
      );

      return;
    }

    console.log(
      "OPENING INVOICE IMAGE:",
      imageUrl
    );

    setCurrentImageUrl(imageUrl);

    setShowImageModal(true);
  };

  // ─────────────────────────────────────────────
  // CLOSE IMAGE MODAL
  // ─────────────────────────────────────────────

  const closeImageModal = () => {
    setShowImageModal(false);
    setCurrentImageUrl(null);
  };

  // ─────────────────────────────────────────────
  // DOWNLOAD IMAGE
  // ─────────────────────────────────────────────

  const handleDownloadImage = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!currentImageUrl) return;

    try {
      const response = await fetch(
        currentImageUrl
      );

      if (!response.ok) {
        throw new Error(
          "Failed to fetch image"
        );
      }

      const blob = await response.blob();

      const blobUrl =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = blobUrl;

      link.download = `invoice-${Date.now()}.jpg`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      toast.success("Image downloaded");
    } catch (error) {
      console.error(
        "DOWNLOAD IMAGE ERROR:",
        error
      );

      window.open(
        currentImageUrl,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  // ─────────────────────────────────────────────
  // CLEANUP
  // ─────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (invoicePreview) {
        URL.revokeObjectURL(invoicePreview);
      }
    };
  }, [invoicePreview]);

  // ─────────────────────────────────────────────
  // LOADING
  // ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="expense-loading">
        Loading...
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  return (
    <div className="expense-page">

      {/* HEADER */}

      <div className="expense-header">
        <div>
          <h1 className="expense-title">
            Expenses
          </h1>

          <p className="expense-subtitle">
            Track spending across categories &amp;
            payment methods
          </p>
        </div>

        <button
          className="add-expense-btn"
          onClick={openAdd}
        >
          + Add Expense
        </button>
      </div>

      {/* STATS */}

      <div className="expense-stats">

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ color: "#facc15" }}
          >
            <FiDollarSign size={24} />
          </div>

          <div>
            <div
              className="stat-value"
              style={{ color: "#facc15" }}
            >
              {formatEGP(totalAmount)}
            </div>

            <div className="stat-label">
              Total Expenses
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ color: "#60a5fa" }}
          >
            <FiFileText size={24} />
          </div>

          <div>
            <div
              className="stat-value"
              style={{ color: "#60a5fa" }}
            >
              {filtered.length}
            </div>

            <div className="stat-label">
              Records
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ color: "#f472b6" }}
          >
            <FiTag size={24} />
          </div>

          <div>
            <div
              className="stat-value"
              style={{ color: "#f472b6" }}
            >
              {topCategory
                ? categoryLabel(topCategory[0])
                : "—"}
            </div>

            <div className="stat-label">
              Top Category
            </div>
          </div>
        </div>

      </div>

      {/* FILTERS */}

      <div className="expense-filters">

        <input
          className="expense-search"
          placeholder="Search description or paid by…"
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        <select
          className="expense-select"
          value={categoryFilter}
          onChange={(e) =>
            setCategoryFilter(e.target.value)
          }
        >
          <option value="All">
            All Categories
          </option>

          {CATEGORIES.map((category) => (
            <option
              key={category.value}
              value={category.value}
            >
              {category.label}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="expense-select"
          value={dateFrom}
          onChange={(e) =>
            setDateFrom(e.target.value)
          }
        />

        <span className="date-sep">
          to
        </span>

        <input
          type="date"
          className="expense-select"
          value={dateTo}
          onChange={(e) =>
            setDateTo(e.target.value)
          }
        />

        {(dateFrom || dateTo) && (
          <button
            className="clear-dates-btn"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
            }}
          >
            Clear dates
          </button>
        )}

      </div>

      {/* TABLE */}

      <div className="expense-table-wrap">

        <table className="expense-table">

          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Paid By</th>
              <th>Method</th>
              <th>Invoice</th>
              <th></th>
            </tr>
          </thead>

          <tbody>

            {filtered.length === 0 ? (

              <tr>
                <td
                  colSpan={8}
                  className="empty-row"
                >
                  No expenses found
                </td>
              </tr>

            ) : (

              filtered.map((expense) => {

                const invoiceUrl =
                  getInvoiceUrl(
                    expense.invoicePhotos
                  );

                return (
                  <tr
                    key={expense._id}
                    onClick={() =>
                      openEdit(expense)
                    }
                    className="expense-row"
                  >

                    <td>
                      {expense.date
                        ? new Date(
                            expense.date
                          ).toLocaleDateString()
                        : "—"}
                    </td>

                    <td>
                      <span className="category-badge">
                        {categoryLabel(
                          expense.category
                        )}
                      </span>
                    </td>

                    <td className="desc-cell">
                      {expense.description || "—"}
                    </td>

                    <td className="num-cell">
                      {formatEGP(
                        expense.amount
                      )}
                    </td>

                    <td>
                      {getUserName(
                        expense.paidBy
                      )}
                    </td>

                    <td>
                      {paymentLabel(
                        expense.paymentMethod
                      )}
                    </td>

                    <td
                      onClick={(event) =>
                        event.stopPropagation()
                      }
                    >

                      {invoiceUrl ? (

                        <button
                          type="button"
                          className="invoice-thumb-btn"
                          onClick={(event) =>
                            openImageModal(
                              invoiceUrl,
                              event
                            )
                          }
                          title="Click to view invoice"
                        >

                          <img
                            src={invoiceUrl}
                            alt="Invoice"
                            className="invoice-thumb"
                            onError={(event) => {
                              console.error(
                                "❌ INVOICE IMAGE FAILED"
                              );

                              console.error(
                                "invoicePhotos:",
                                expense.invoicePhotos
                              );

                              console.error(
                                "invoiceUrl:",
                                invoiceUrl
                              );

                              event.currentTarget.style.display =
                                "none";
                            }}
                          />

                        </button>

                      ) : (

                        <span className="no-invoice">
                          —
                        </span>

                      )}

                    </td>

                    <td
                      onClick={(event) =>
                        event.stopPropagation()
                      }
                    >

                      <button
                        className="del-btn"
                        onClick={(event) =>
                          handleDelete(
                            expense._id,
                            event
                          )
                        }
                        title="Delete"
                      >
                        <FiTrash size={16} />
                      </button>

                    </td>

                  </tr>
                );
              })

            )}

          </tbody>

        </table>

      </div>

      {/* ADD / EDIT MODAL */}

      {showModal && (

        <div
          className="modal-overlay"
          onClick={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setShowModal(false);
            }
          }}
        >

          <div
            className="modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            {/* MODAL HEADER */}

            <div className="modal-header">

              <h3>
                {editingId
                  ? "Edit Expense"
                  : "Add Expense"}
              </h3>

              <button
                className="modal-close"
                onClick={() =>
                  setShowModal(false)
                }
              >
                ✕
              </button>

            </div>

            {/* MODAL BODY */}

            <div className="modal-body">

              {/* DATE */}

              <div className="modal-field">

                <label>
                  Date *
                </label>

                <input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      date: e.target.value,
                    })
                  }
                />

                {errors.date && (
                  <div className="field-error">
                    {errors.date}
                  </div>
                )}

              </div>

              {/* CATEGORY */}

              <div className="modal-field">

                <label>
                  Category *
                </label>

                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      category:
                        e.target.value,
                    })
                  }
                >

                  <option value="">
                    — Select category —
                  </option>

                  {CATEGORIES.map(
                    (category) => (
                      <option
                        key={category.value}
                        value={category.value}
                      >
                        {category.label}
                      </option>
                    )
                  )}

                </select>

                {errors.category && (
                  <div className="field-error">
                    {errors.category}
                  </div>
                )}

              </div>

              {/* DESCRIPTION */}

              <div className="modal-field">

                <label>
                  Description
                </label>

                <input
                  value={form.description}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      description:
                        e.target.value,
                    })
                  }
                  placeholder="e.g. July office rent"
                />

              </div>

              {/* AMOUNT */}

              <div className="modal-field">

                <label>
                  Amount (EGP) *
                </label>

                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      amount:
                        e.target.value,
                    })
                  }
                  placeholder="0.00"
                />

                {errors.amount && (
                  <div className="field-error">
                    {errors.amount}
                  </div>
                )}

              </div>

              {/* PAID BY */}

              <div className="modal-field">

                <label>
                  Paid By *
                </label>

                <select
                  value={form.paidBy}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      paidBy:
                        e.target.value,
                    })
                  }
                >

                  <option value="">
                    — Select employee —
                  </option>

                  {users.map((user) => (
                    <option
                      key={user._id}
                      value={user._id}
                    >
                      {user.userName ||
                        user.name}
                    </option>
                  ))}

                </select>

                {errors.paidBy && (
                  <div className="field-error">
                    {errors.paidBy}
                  </div>
                )}

              </div>

              {/* PAYMENT METHOD */}

              <div className="modal-field">

                <label>
                  Payment Method *
                </label>

                <select
                  value={
                    form.paymentMethod
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      paymentMethod:
                        e.target.value,
                    })
                  }
                >

                  <option value="">
                    — Select method —
                  </option>

                  {PAYMENT_METHODS.map(
                    (method) => (
                      <option
                        key={method.value}
                        value={method.value}
                      >
                        {method.label}
                      </option>
                    )
                  )}

                </select>

                {errors.paymentMethod && (
                  <div className="field-error">
                    {errors.paymentMethod}
                  </div>
                )}

              </div>

              {/* NOTES */}

              <div className="modal-field">

                <label>
                  Notes
                </label>

                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      notes: e.target.value,
                    })
                  }
                />

              </div>

              {/* ───────────────── INVOICE UPLOAD ───────────────── */}

              <div className="modal-field">

                <label>
                  Invoice Photo
                </label>

                {/* SELECTED NEW IMAGE */}

                {invoicePreview ? (

                  <div className="invoice-preview-wrap">

                    <div
                      className="invoice-preview-clickable"
                      onClick={(event) =>
                        openImageModal(
                          invoicePreview,
                          event
                        )
                      }
                    >

                      <img
                        src={invoicePreview}
                        alt="Invoice preview"
                        className="invoice-preview-img"
                      />

                      <div className="invoice-preview-overlay">
                        <FiImage size={22} />

                        <span>
                          Click to view
                        </span>
                      </div>

                    </div>

                    <button
                      type="button"
                      className="file-action-btn"
                      onClick={
                        removeInvoiceFile
                      }
                    >
                      Remove
                    </button>

                  </div>

                ) : existingInvoiceUrl ? (

                  /* EXISTING IMAGE */

                  <div className="invoice-preview-wrap">

                    <div
                      className="invoice-preview-clickable"
                      onClick={(event) =>
                        openImageModal(
                          existingInvoiceUrl,
                          event
                        )
                      }
                    >

                      <img
                        src={existingInvoiceUrl}
                        alt="Current invoice"
                        className="invoice-preview-img"
                        onError={() => {
                          console.error(
                            "❌ EXISTING INVOICE IMAGE FAILED:"
                          );

                          console.error(
                            existingInvoiceUrl
                          );
                        }}
                      />

                      <div className="invoice-preview-overlay">
                        <FiImage size={22} />

                        <span>
                          Click to view
                        </span>
                      </div>

                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                      }}
                    >

                      <button
                        type="button"
                        className="file-action-btn"
                        onClick={
                          openFilePicker
                        }
                      >
                        Replace
                      </button>

                      <button
                        type="button"
                        className="file-action-btn file-action-danger"
                        onClick={
                          removeExistingInvoice
                        }
                      >
                        <FiTrash
                          size={14}
                          style={{
                            marginRight: 4,
                          }}
                        />
                        Delete
                      </button>

                    </div>

                  </div>

                ) : (

                  /* NO IMAGE */

                  <div
                    className="upload-drop-zone"
                    role="button"
                    tabIndex={0}
                    onClick={
                      openFilePicker
                    }
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" ||
                        event.key === " "
                      ) {
                        event.preventDefault();

                        openFilePicker();
                      }
                    }}
                  >

                    <FiUploadCloud
                      size={32}
                    />

                    <div>
                      Click to upload invoice photo
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.6,
                        marginTop: 4,
                      }}
                    >
                      JPG, PNG, WEBP
                    </div>

                  </div>

                )}

                {/* ONE FILE INPUT */}

                <input
                  ref={fileRef}
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFilePick}
                />

              </div>

            </div>

            {/* MODAL FOOTER */}

            <div className="modal-footer">

              <button
                className="modal-cancel"
                onClick={() =>
                  setShowModal(false)
                }
              >
                Cancel
              </button>

              <button
                className="modal-save"
                onClick={handleSave}
                disabled={submitting}
              >
                {submitting
                  ? "Saving…"
                  : editingId
                  ? "Save Changes"
                  : "Create Expense"}
              </button>

            </div>

          </div>

        </div>

      )}

      {/* IMAGE VIEW MODAL */}

      {showImageModal &&
        currentImageUrl && (

          <div
            className="image-modal-overlay"
            onClick={(event) => {

              if (
                event.target ===
                event.currentTarget
              ) {
                closeImageModal();
              }

            }}
          >

            <div
              className="image-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >

              <div className="image-modal-header">

                <h3>
                  Invoice Photo
                </h3>

                <button
                  className="modal-close"
                  onClick={
                    closeImageModal
                  }
                >
                  ✕
                </button>

              </div>

              <div className="image-modal-body">

                <img
                  src={currentImageUrl}
                  alt="Invoice"
                  className="full-size-image"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    display: "block",
                    margin: "0 auto",
                  }}
                  onError={() => {
                    console.error(
                      "❌ FULL IMAGE FAILED:"
                    );

                    console.error(
                      currentImageUrl
                    );
                  }}
                />

              </div>

              <div className="image-modal-footer">

                <button
                  className="modal-cancel"
                  onClick={
                    closeImageModal
                  }
                >
                  Close
                </button>

                <button
                  className="modal-save"
                  onClick={
                    handleDownloadImage
                  }
                >
                  <FiDownload
                    size={16}
                    style={{
                      marginRight: 6,
                    }}
                  />

                  Download
                </button>

              </div>

            </div>

          </div>

        )}

    </div>
  );
}